/**
 * Reader feedback client (Task 251 Layer 2): one rating per answer, sent to the
 * proxy's POST <answer>/feedback. Plain functions, no React state — the
 * FeedbackControls component owns the UI state and calls these.
 *
 * Privacy: text leaves the browser ONLY when the reader ticked «Incluir mi
 * pregunta y la respuesta…» (unchecked by default): the question, the answer
 * and, riding with them, the optional note. The reasons are fixed codes.
 */
import type { AnswerCitation } from "./answer-client";

export type FeedbackRating = "useful" | "not_useful";
export type FeedbackReason = "incorrect_teaching" | "missing_teaching" | "unrelated_source" | "too_slow";
export const FEEDBACK_REASONS: readonly FeedbackReason[] = ["incorrect_teaching", "missing_teaching", "unrelated_source", "too_slow"];
export const FEEDBACK_NOTE_MAX = 500;
const SHARED_SOURCES_MAX = 8;

export interface FeedbackPayload {
  request_id: string;
  rating: FeedbackRating;
  language: "es" | "en";
  reasons?: FeedbackReason[];
  note?: string;
  shared?: { question: string; answer: string; sources: Array<{ title: string; path: string; timestamp_start?: number }> };
}

export interface FeedbackDraft {
  requestId: string;
  rating: FeedbackRating;
  language: "es" | "en";
  reasons?: readonly FeedbackReason[];
  note?: string;
  /** The reader ticked the box: send the question and the answer too. */
  share?: boolean;
  question?: string;
  answer?: string;
  sources?: readonly AnswerCitation[];
}

/** Build the wire payload. Reasons and shared content only ride a «No útil»;
 *  the note only rides the shared conversation, never alone. */
export function buildFeedbackPayload(draft: FeedbackDraft): FeedbackPayload {
  const payload: FeedbackPayload = { request_id: draft.requestId, rating: draft.rating, language: draft.language };
  if (draft.rating !== "not_useful") return payload;
  const reasons = [...new Set(draft.reasons ?? [])].filter((r) => FEEDBACK_REASONS.includes(r));
  if (reasons.length > 0) payload.reasons = reasons;
  const question = (draft.question ?? "").trim();
  const answer = (draft.answer ?? "").trim();
  if (draft.share === true && question.length > 0 && answer.length > 0) {
    const note = (draft.note ?? "").trim().slice(0, FEEDBACK_NOTE_MAX);
    if (note.length > 0) payload.note = note;
    payload.shared = {
      question,
      answer,
      sources: (draft.sources ?? []).slice(0, SHARED_SOURCES_MAX).map((s) => ({
        title: s.title,
        path: s.path,
        ...(typeof s.timestamp_start === "number" ? { timestamp_start: s.timestamp_start } : {}),
      })),
    };
  }
  return payload;
}

export type FeedbackResult = { ok: true } | { ok: false; kind: "rate_limit" | "invalid" | "network" | "unavailable" };

export async function postFeedback(endpoint: string, payload: FeedbackPayload): Promise<FeedbackResult> {
  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    return { ok: false, kind: "network" };
  }
  if (response.ok) return { ok: true };
  if (response.status === 429) return { ok: false, kind: "rate_limit" };
  if (response.status === 400) return { ok: false, kind: "invalid" };
  return { ok: false, kind: "unavailable" };
}
